/**
 * Conversation View Component
 * Full conversation display with RTF reply composer
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Mail, Send, X, Paperclip, Loader2, Sparkles, Reply, MoreHorizontal, Calendar } from 'lucide-react';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { auth } from '../../../utils/auth';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { MessageBubble } from './MessageBubble';
import { groupMessagesByDate, getDateLabel, replacePlaceholders } from './utils';
import type { Thread, ClientData, CaseData } from './types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface ConversationViewProps {
  thread: Thread | null;
  client: ClientData | null;
  caseId: string;
  caseData: CaseData | null;
  onReply?: (message: string, thread: Thread) => void;
}

export const ConversationView: React.FC<ConversationViewProps> = ({ 
  thread, 
  client, 
  caseId, 
  caseData, 
  onReply 
}) => {
  const { t, i18n } = useTranslation('caseWorkspace');
  const [replyText, setReplyText] = useState('');
  const [generatingReply, setGeneratingReply] = useState(false);
  const [showFullComposer, setShowFullComposer] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  
  // Auto-replace placeholders
  useEffect(() => {
    if (replyText.includes('[')) {
      const replaced = replacePlaceholders(replyText, client, caseData);
      if (replaced !== replyText) {
        setReplyText(replaced);
      }
    }
  }, [replyText, client, caseData]);
  
  // RTF formatting
  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };
  
  const toggleBold = () => { formatText('bold'); setIsBold(!isBold); };
  const toggleItalic = () => { formatText('italic'); setIsItalic(!isItalic); };
  const toggleUnderline = () => { formatText('underline'); setIsUnderline(!isUnderline); };
  const insertBulletList = () => formatText('insertUnorderedList');
  const insertNumberedList = () => formatText('insertOrderedList');
  
  const messagesByDate = useMemo(() => {
    if (!thread) return {};
    const sorted = [...thread.messages].sort((a, b) => 
      new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
    );
    return groupMessagesByDate(sorted);
  }, [thread]);
  
  const updateEditorContent = useCallback((content: string) => {
    if (editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content;
    }
  }, []);
  
  const generateAIReply = async () => {
    if (!thread || !caseId) return;
    setGeneratingReply(true);
    
    try {
      const token = auth.getToken();
      const lastClientMsg = thread.messages.find(m => 
        m.direction === 'INCOMING' || m.direction === 'INBOUND'
      );
      
      const response = await axios.post(
        `${API}/communications/ai/generate-reply`,
        {
          case_id: caseId,
          thread_subject: thread.subject,
          last_message: lastClientMsg?.body || lastClientMsg?.body_preview || '',
          tone: 'professional'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.reply) {
        const processedReply = replacePlaceholders(response.data.reply, client, caseData);
        const htmlContent = processedReply.replace(/\n/g, '<br>');
        setReplyText(htmlContent);
        setShowFullComposer(true);
        setTimeout(() => updateEditorContent(htmlContent), 100);
        toast.success(response.data.ai_powered ? t('communication.aiDraftGenerated') : t('communication.templateLoaded'));
      }
    } catch (error) {
      console.error('Failed to generate reply:', error);
      toast.error(t('communication.couldNotGenerateReply'));
    } finally {
      setGeneratingReply(false);
    }
  };
  
  const handleSendReply = () => {
    const textContent = editorRef.current?.innerHTML || replyText;
    if (textContent.trim() && thread) {
      onReply?.(textContent, thread);
      setReplyText('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      setShowFullComposer(false);
    }
  };
  
  // Empty state
  if (!thread) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-gray-50/30 to-white h-full min-h-[500px]">
        <div className="text-center max-w-xs">
          <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-sm">
            <Mail className="w-9 h-9 text-gray-300" />
          </div>
          <h3 className="text-gray-700 font-semibold mb-2">{t('communication.selectConversation')}</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            {t('communication.selectConversationDescription', 'Choose a conversation from the list to view messages and reply')}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{thread.subject}</h3>
            <p className="text-xs text-slate-500">
              {thread.messages.length === 1 
                ? t('communication.messagesInThread', { count: thread.messages.length })
                : t('communication.messagesInThreadPlural', { count: thread.messages.length })}
              {thread.needsResponse && (
                <span className="ml-2 text-amber-600">· {t('communication.awaitingResponseLabel')}</span>
              )}
            </p>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {Object.entries(messagesByDate).map(([date, msgs]) => (
          <div key={date}>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">
                {getDateLabel(date, t, i18n.language)}
              </span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.id || i} message={msg} isLast={i === msgs.length - 1} />
            ))}
          </div>
        ))}
      </ScrollArea>
      
      {/* Reply area */}
      <div className="flex-shrink-0 border-t border-gray-100 bg-gradient-to-b from-gray-50/50 to-white">
        {showFullComposer ? (
          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Reply className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {t('communication.replyTo')}: {client?.name || t('communication.client')}
                </span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowFullComposer(false)}>
                <X className="w-4 h-4 text-gray-500" />
              </Button>
            </div>
            
            {/* RTF Toolbar */}
            <div className="flex items-center gap-1 p-1.5 bg-gray-100 rounded-xl">
              <button onClick={toggleBold} className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-all", isBold ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:bg-white/50")} title={t('communication.bold', 'Bold')}>
                <span className="font-bold text-sm">B</span>
              </button>
              <button onClick={toggleItalic} className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-all", isItalic ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:bg-white/50")} title={t('communication.italic', 'Italic')}>
                <span className="italic text-sm">I</span>
              </button>
              <button onClick={toggleUnderline} className={cn("w-8 h-8 rounded-lg flex items-center justify-center transition-all", isUnderline ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:bg-white/50")} title={t('communication.underline', 'Underline')}>
                <span className="underline text-sm">U</span>
              </button>
              <div className="w-px h-5 bg-gray-300 mx-1" />
              <button onClick={insertBulletList} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-white/50 transition-all" title={t('communication.bulletList', 'Bullet list')}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" /><circle cx="4" cy="6" r="2" fill="currentColor" /><circle cx="4" cy="12" r="2" fill="currentColor" /><circle cx="4" cy="18" r="2" fill="currentColor" /></svg>
              </button>
              <button onClick={insertNumberedList} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-white/50 transition-all" title={t('communication.numberedList', 'Numbered list')}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><text x="3" y="7" fontSize="6" fill="currentColor">1</text><text x="3" y="13" fontSize="6" fill="currentColor">2</text><text x="3" y="19" fontSize="6" fill="currentColor">3</text></svg>
              </button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" className="h-8 px-3 text-violet-600 hover:bg-violet-50 rounded-lg" onClick={generateAIReply} disabled={generatingReply}>
                {generatingReply ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                {t('communication.improveWithAI', 'Improve with AI')}
              </Button>
            </div>
            
            {/* Editor */}
            <div className="relative">
              <div
                ref={editorRef}
                contentEditable
                className="w-full min-h-[140px] max-h-[300px] overflow-y-auto px-4 py-3 bg-white rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                onInput={(e) => setReplyText((e.target as HTMLDivElement).innerHTML)}
                suppressContentEditableWarning
                style={{ lineHeight: '1.6' }}
              />
              {!replyText && (
                <div className="absolute top-3 left-4 text-gray-400 text-sm pointer-events-none">
                  {t('communication.writeYourReply', 'Write your reply...')}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" size="sm" className="h-9 px-3 rounded-lg hover:bg-gray-100">
                <Paperclip className="w-4 h-4 mr-1.5 text-gray-500" />
                <span className="text-gray-600">{t('communication.attachment', 'Attach')}</span>
              </Button>
              <Button size="sm" className="h-10 px-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl shadow-lg shadow-blue-500/20" onClick={handleSendReply} disabled={!replyText.trim()}>
                <Send className="w-4 h-4 mr-2" />
                {t('common.send', 'Send')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="h-10 w-10 p-0 rounded-xl hover:bg-gray-100 flex-shrink-0">
                <Paperclip className="w-5 h-5 text-gray-400" />
              </Button>
              <div className="flex-1">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`${t('communication.replyTo')} ${client?.name || t('communication.client')}...`}
                  className="w-full h-12 px-5 rounded-2xl bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm hover:shadow-md transition-all"
                  onKeyDown={(e) => e.key === 'Enter' && replyText.trim() && handleSendReply()}
                  onFocus={() => setShowFullComposer(true)}
                />
              </div>
              <Button size="sm" className="h-12 w-12 p-0 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/20" onClick={handleSendReply} disabled={!replyText.trim()}>
                <Send className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-3 pl-14">
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 hover:bg-violet-50 rounded-lg transition-all" onClick={generateAIReply} disabled={generatingReply}>
                {generatingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {t('communication.aiDraft', 'AI Draft')}
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 rounded-lg transition-all">
                <Calendar className="w-3.5 h-3.5" />
                {t('communication.reminder', 'Reminder')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationView;
